/**
 * Default di branding del template e utilità di lettura da `.env`.
 *
 * Il modulo è condiviso tra il codice applicativo (`src/app/config.ts`, eseguito
 * nel browser) e la build (`vite.config.ts`, eseguita da Node), che usa gli stessi
 * default per i placeholder di `index.html`.
 */
export interface BrandConfig {
    name: string;
    tagline: string;
    websiteUrl: string;
    /** Partita IVA (opzionale): se `VITE_APP_VAT` è assente o vuota non viene mostrata. */
    vat?: string;
    credits: string;
    logo: string;
    favicon: string;
    colors: {
        primary: string;
        /** Alone di sfondo (gradiente della pagina). */
        secondary: string;
        /** Testi evidenziati e icone (`text-accent`). */
        accent: string;
        background: string;
        surface: string;
    };
}

export const BRAND_DEFAULTS: BrandConfig = {
    name: 'Karaoke in Movimento',
    tagline: 'Karaoke & DJ in Movimento',
    websiteUrl: 'https://example.com',
    credits: 'Made by YourName',
    logo: '/assets/logo.svg',
    favicon: '/assets/logo.svg',
    colors: {
        primary: '#FF005C',
        secondary: '#E83A8B',
        accent: '#E83A8B',
        background: '#14040c',
        surface: '#17171a',
    },
};

/** Valore testuale da `.env`: vuoto (o di soli spazi) → default del template. */
export function resolveText(value: string | undefined, fallback: string): string {
    return value?.trim() || fallback;
}

/**
 * Testo opzionale da `.env`: assente o vuoto → `undefined`, così chi lo consuma
 * può nasconderlo invece di mostrare un fallback (es. `VITE_APP_VAT` nel footer).
 */
export function resolveOptionalText(value: string | undefined): string | undefined {
    return value?.trim() || undefined;
}

/**
 * Colore da `.env`.
 *
 * Attenzione: in `.env` il `#` apre un commento, quindi i colori vanno tra apici
 * (`VITE_APP_PRIMARY="#BF9A33"`), altrimenti Vite legge una stringa vuota.
 * Per comodità accettiamo anche l'esadecimale senza `#` (`VITE_APP_PRIMARY=BF9A33`).
 */
export function resolveColor(value: string | undefined, fallback: string): string {
    const raw = value?.trim();
    if (!raw) return fallback;
    if (raw.startsWith('#')) return raw;
    return /^[0-9a-fA-F]{3,8}$/.test(raw) ? `#${raw}` : raw;
}

/**
 * Colore dell'alone di sfondo (gradiente di `body` in `globals.css`): `VITE_APP_SECONDARY`
 * se valorizzato, altrimenti `VITE_APP_PRIMARY`, altrimenti il default del template.
 */
export function resolveAccent(secondary: string | undefined, primary: string | undefined): string {
    return resolveColor(secondary?.trim() || primary, BRAND_DEFAULTS.colors.secondary);
}

/**
 * Colore dei testi evidenziati (`text-accent`): `VITE_APP_ACCENT` se valorizzato,
 * altrimenti segue l'alone di sfondo (`resolveAccent`). Così i `.env` esistenti non
 * cambiano aspetto, e il testo può avere un colore diverso dallo sfondo.
 */
export function resolveTextAccent(
    accent: string | undefined,
    secondary: string | undefined,
    primary: string | undefined
): string {
    return accent?.trim() ? resolveColor(accent, BRAND_DEFAULTS.colors.accent) : resolveAccent(secondary, primary);
}
